import DynamicModal from 'components/DynamicModal';
import { useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { FormEvent, useEffect, useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import { Button, Form, FormGroup, Input, Label } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { getKPIErrorMessage } from './helper';
import styles from './styles.module.css';
import { KPIManualData, KPISection } from './types';

interface ManualDataModalProps {
  isOpen: boolean;
  toggle: () => void;
  defaultPeriodEnd: string;
  section: KPISection | null;
  onSaved: () => void;
}

type FormValues = Record<string, string>;

const ManualDataModal = ({
  isOpen,
  toggle,
  defaultPeriodEnd,
  section,
  onSaved,
}: ManualDataModalProps) => {
  const formId = 'kpi-manual-data-form';
  const companyId = useAtomValue(companyIdAtom);
  const [periodEnd, setPeriodEnd] = useState(defaultPeriodEnd);
  const [values, setValues] = useState<FormValues>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const visibleDefinitions = section?.metrics || [];
  const sectionLabel = section?.label || 'KPI';

  useEffect(() => {
    if (isOpen) setPeriodEnd(defaultPeriodEnd);
  }, [defaultPeriodEnd, isOpen]);

  useEffect(() => {
    if (!isOpen || !periodEnd || companyId <= 0) return undefined;

    let mounted = true;
    setLoading(true);
    setValues({});
    // Native date inputs can emit several changes while a date is being edited.
    // Debouncing avoids overlapping API calls and lets callApi cancel stale reads.
    const loadTimeout = window.setTimeout(() => {
      callApi(
        'dashboard/kpi/manual/',
        'GET',
        { company_id: companyId, period_end: periodEnd },
        null,
        null,
        'dashboard/kpi/manual/:kpi-manual-read',
      )
        .then((response) => {
          if (!mounted || !response?.data) return;
          const manualData = response.data as KPIManualData;
          const nextValues: FormValues = {};
          manualData.values.forEach((metric) => {
            nextValues[metric.metric_key] = String(metric.value);
          });
          setValues(nextValues);
        })
        .catch((error) => {
          if (mounted) {
            showFailureToast(
              getKPIErrorMessage(error, 'Unable to load manual KPI values.'),
            );
          }
        })
        .finally(() => {
          if (mounted) setLoading(false);
        });
    }, 300);

    return () => {
      mounted = false;
      window.clearTimeout(loadTimeout);
    };
  }, [companyId, isOpen, periodEnd, section?.key]);

  const updateValue = (metricKey: string, value: string) => {
    setValues((current) => ({ ...current, [metricKey]: value }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const submittedValues = visibleDefinitions
      .filter(
        (metric) =>
          values[metric.key] !== undefined && values[metric.key] !== '',
      )
      .map((metric) => ({
        metric_key: metric.key,
        value: Number(values[metric.key]),
      }));

    if (submittedValues.length === 0) {
      showFailureToast('At least one KPI value is required.');
      return;
    }

    setSaving(true);
    try {
      await callApi(
        'dashboard/kpi/manual/',
        'PUT',
        {},
        {
          company_id: companyId,
          period_end: periodEnd,
          values: submittedValues,
        },
        null,
        'dashboard/kpi/manual/:kpi-manual-save',
      );
      showSuccessToast(`${sectionLabel} KPI values updated successfully.`);
      onSaved();
      toggle();
    } catch (error) {
      showFailureToast(
        getKPIErrorMessage(error, 'Unable to update KPI values.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <Form className={styles.modalBody} id={formId} onSubmit={handleSubmit}>
      <FormGroup className={styles.periodField}>
        <Label for="kpi-period-end">Reporting period end</Label>
        <Input
          id="kpi-period-end"
          type="date"
          value={periodEnd}
          onChange={(event) => setPeriodEnd(event.target.value)}
          required
        />
      </FormGroup>

      {loading ? (
        <div className={styles.modalLoading}>
          <CircleSpinner size={24} color="#0665a9" loading={true} />
        </div>
      ) : (
        <section className={styles.formSection}>
          <div className={styles.formGrid}>
            {visibleDefinitions.map((metric) => (
              <FormGroup key={metric.key}>
                <Label for={`kpi-${metric.key}`}>{metric.label}</Label>
                <Input
                  id={`kpi-${metric.key}`}
                  type="number"
                  min="0"
                  max={metric.unit === 'percent' ? '100' : undefined}
                  step={metric.unit === 'count' ? '1' : '0.01'}
                  value={values[metric.key] || ''}
                  onChange={(event) =>
                    updateValue(metric.key, event.target.value)
                  }
                />
              </FormGroup>
            ))}
          </div>
        </section>
      )}
    </Form>
  );
  const footerActions = (
    <>
      <Button
        color="secondary"
        type="button"
        onClick={toggle}
        disabled={saving}
      >
        Cancel
      </Button>
      {saving ? (
        <CircleSpinner size={20} color="#0665a9" loading={true} />
      ) : (
        <Button color="primary" type="submit" form={formId} disabled={loading}>
          Save
        </Button>
      )}
    </>
  );

  return (
    <DynamicModal
      isOpen={isOpen}
      toggle={toggle}
      title={`Update ${sectionLabel} Data`}
      content={content}
      footerActions={footerActions}
      size="lg"
    />
  );
};

export default ManualDataModal;
